import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
 Calendar,
 CheckCircle,
 ChevronRight,
 Copy,
 Download,
 ExternalLink,
 Flag,
 Link2,
 Loader2,
 Mail,
 MailOpen,
 MonitorPlay,
 Settings2,
 Sparkles,
 User,
 XCircle,
} from 'lucide-react';
import { PIPELINE_STATUS_LABEL } from '@core/constants/pipelineStages';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import {
 aiInterviewSessionService,
 type AiInterviewSessionHubDetailRow,
 type AiInterviewSessionWithArtifacts,
} from '@core/supabase/services/aiInterviewSessionService';
import { applicationService } from '@core/supabase/services/applicationService';
import { candidateService } from '@core/supabase/services/candidateService';
import { emailService } from '@core/supabase/services/emailService';
import { interviewEventService, type InterviewEvent } from '@core/supabase/services/interviewEventService';
import type { TemplateVars } from '@core/email/templates';
import type { Application, Candidate, EmailThread } from '@core/types';
import { RecordHeader } from '@shared/components/ui/RecordHeader';
import { StatusChip, statusDotColorFromHint } from '@shared/components/ui/StatusChip';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { InterviewMonthCalendar, type MonthCalendarEvent } from '@modules/interviews/InterviewMonthCalendar';
import { AiInterviewScheduleWizardModal } from './AiInterviewScheduleWizardModal';
import { CandidateAiInterviewsPanel } from '@modules/candidates/CandidateAiInterviewsPanel';
import { CandidateCollapsiblePanel } from '@modules/candidates/CandidateCollapsiblePanel';
import { CandidateForm } from '@modules/candidates/CandidateForm';
import { CandidateProfileSummary } from '@modules/candidates/CandidateProfileSummary';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import {
 AI_INTERVIEW_SESSION_TABS,
 parseAiInterviewSessionTab,
 type AiInterviewSessionTabId,
} from './aiInterviewSessionDetailTabs';
import { EmailComposeView } from '@modules/email/EmailComposePage';
import { AiInterviewExperienceModal } from '@modules/public/ai-interview/AiInterviewExperienceModal';
import { postInterviewExperienceFeedback } from '@modules/public/ai-interview/publicInterviewApi';
import { DEFAULT_RUBRIC, computeCompositeScore } from '@core/ai/interviewAssessment';
import { listInterviewAttempts, transcriptLabel } from './aiInterviewArtifacts';
import {
 hasMeaningfulAssessment,
 resolveReviewerSessionStatus,
 reviewerListScoreDisplay,
 reviewerSessionDateIso,
 reviewerStatusChipLabel,
} from './aiInterviewReviewerUi';
import {
 InterviewSummaryTabs,
 type InterviewSummaryTabId,
} from '@modules/interviews/components/InterviewSummaryTabs';
import { InterviewTemplateRail } from '@modules/interviews/components/InterviewTemplateRail';

function interviewPrepUrl(token: string): string {
 if (typeof window === 'undefined') return '';
 return `${window.location.origin}/interview/prep/${token}`;
}

/** Shared dt/dd styles for Parameters tab — mirrors InterviewCandidateDetailPage */
const PARAM_DT = 'vscode-text-secondary pr-2 min-w-0';
const PARAM_DD = 'vscode-text-primary min-w-0 break-words';

function fmtDateLong(iso: string | null | undefined): string {
 return formatDateTimeGmt(iso);
}

const ROUND_LABEL: Record<string, string> = {
 screening: 'Screening',
 technical: 'Technical',
 behavioral: 'Behavioral',
};

function recordInitials(name: string) {
 const p = name.trim().split(/\s+/).filter(Boolean);
 if (p.length === 0) return '?';
 if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
 return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

function aiInterviewSessionStatusLabel(status: string): string {
 const m: Record<string, string> = {
 draft: 'Draft',
 invited: 'Invited',
 in_progress: 'In progress',
 completed: 'Completed',
 cancelled: 'Cancelled',
 };
 return m[status] ?? status;
}

/** Matches Email tab right rail (`ggj-text-meta`, muted label / primary value). */
function AiInterviewSessionRailRow({ label, children }: { label: string; children: ReactNode }) {
 return (
 <div className="flex justify-between gap-2 items-start ggj-text-meta">
 <span className="vscode-text-tertiary shrink-0">{label}</span>
 <span className="font-medium vscode-text-primary text-right min-w-0 break-words">{children}</span>
 </div>
 );
}

const BRAND_CAL = '#8B80F9';

function toLocalDayKey(d: Date): string {
 return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function CalendarRoundStatusBadge({ status }: { status: string }) {
 const cfg: Record<string, { bg: string; color: string }> = {
 conducted: { bg: 'rgba(34,197,94,0.12)', color: '#22c55e' },
 scheduled: { bg: `${BRAND_CAL}18`, color: BRAND_CAL },
 awaiting_booking: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
 no_show: { bg: 'rgba(239,68,68,0.12)', color: '#ef4444' },
 cancelled: { bg: 'rgba(100,116,139,0.15)', color: '#94a3b8' },
 draft: { bg: 'rgba(100,116,139,0.10)', color: '#94a3b8' },
 };
 const c = cfg[status] ?? { bg: 'rgba(100,116,139,0.10)', color: '#94a3b8' };
 return (
 <span
 className="ggj-text-meta px-2 py-0.5 rounded-full font-medium capitalize inline-block"
 style={{ background: c.bg, color: c.color }}
 >
 {status.replace(/_/g, ' ')}
 </span>
 );
}

function buildApplicationRefDisplay(applicationId: string, clientId: string): string {
 const y = new Date().getFullYear();
 return `GGJ-${y}-${String(clientId).slice(0, 4).toUpperCase()}-${applicationId.slice(0, 6).toUpperCase()}`;
}

function SessionEmailAttrRow({
 label,
 children,
 ddClassName,
}: {
 label: string;
 children: React.ReactNode;
 ddClassName?: string;
}) {
 return (
 <>
 <dt className="vscode-text-secondary pr-2 min-w-0">{label}</dt>
 <dd className={`vscode-text-primary min-w-0 break-words ${ddClassName ?? ''}`}>{children}</dd>
 </>
 );
}

function SessionEmailAttrGroup({ title, children }: { title: ReactNode; children: React.ReactNode }) {
 return (
 <div className="ggj-param-group">
 <div className="ggj-profile-section-h">{title}</div>
 <div className="ggj-param-group-body">{children}</div>
 </div>
 );
}

function eventStatusLabel(s: string): string {
 const m: Record<string, string> = {
 draft: 'Draft',
 awaiting_booking: 'Awaiting booking',
 scheduled: 'Scheduled',
 conducted: 'Conducted',
 cancelled: 'Cancelled',
 no_show: 'No-show',
 };
 return m[s] ?? s;
}

const RAIL_PARAM_DT = `${PARAM_DT} text-[10px] uppercase tracking-wide font-semibold`;
const RAIL_PARAM_DD = `${PARAM_DD} ggj-text-meta`;

/** Calendar tab right rail: mirrors Parameters (Object → session → audit) in a narrow column. */
function AiInterviewCalendarRailPanel({
 session,
 candidate,
 candidateName,
 applicationSnapshot,
 orgId,
 onCopyPrep,
 onEmailTab,
 onParametersTab,
}: {
 session: AiInterviewSessionHubDetailRow;
 candidate: Candidate;
 candidateName: string;
 applicationSnapshot: Application | null;
 orgId: string | undefined;
 onCopyPrep: () => void;
 onEmailTab: () => void;
 onParametersTab: () => void;
}) {
 const descriptionValue = candidate.notes?.trim()
 ? candidate.notes
 : [
 `Browser AI interview (${ROUND_LABEL[session.session_type] ?? session.session_type}).`,
 session.application_id ? 'Linked to a pipeline application when present.' : 'No application linked on this session row.',
 ].join(' ');

 return (
 <div className="space-y-3 ggj-text-meta">
 <div className="ggj-param-group overflow-hidden">
 <div className="ggj-param-section-block border-b border-[color-mix(in_srgb,var(--vscode-border-primary)_55%,transparent)]">
 <div className="ggj-profile-section-h !text-[11px]">Object</div>
 <div className="ggj-profile-section-body !px-2.5 !py-2">
 <dl className="ggj-attr-grid gap-y-2">
 <dt className={RAIL_PARAM_DT}>Name</dt>
 <dd className={`${RAIL_PARAM_DD} font-medium`}>
 {[candidateName, applicationSnapshot?.job?.title].filter(Boolean).join(' — ') || '—'}
 </dd>
 <dt className={RAIL_PARAM_DT}>Object type</dt>
 <dd className={`${RAIL_PARAM_DD} vscode-text-secondary`}>AI interview session (read-only)</dd>
 <dt className={`${RAIL_PARAM_DT} lg:col-span-1`}>Description</dt>
 <dd className={`${RAIL_PARAM_DD} min-w-0 lg:col-span-3`}>
 <textarea
 readOnly
 tabIndex={-1}
 rows={4}
 value={descriptionValue}
 aria-label="Description"
 className="ggj-param-readonly-textarea ggj-text-meta"
 />
 </dd>
 </dl>
 </div>
 </div>
 </div>

 <div className="ggj-param-group overflow-hidden">
 <div className="ggj-profile-section-h !text-[11px]">AI interview session</div>
 <div className="ggj-profile-section-body !px-2.5 !py-2">
 <dl className="ggj-attr-grid gap-y-1.5">
 <dt className={RAIL_PARAM_DT}>Status</dt>
 <dd className={RAIL_PARAM_DD}>
 <span className="capitalize">{session.status.replace(/_/g, ' ')}</span>
 </dd>
 <dt className={RAIL_PARAM_DT}>Round</dt>
 <dd className={RAIL_PARAM_DD}>{ROUND_LABEL[session.session_type] ?? session.session_type}</dd>
 <dt className={RAIL_PARAM_DT}>Language</dt>
 <dd className={RAIL_PARAM_DD}>{session.language}</dd>
 <dt className={RAIL_PARAM_DT}>Prep link</dt>
 <dd className={RAIL_PARAM_DD}>
 <button
 type="button"
 className="text-[var(--vscode-text-link)] hover:underline text-left"
 onClick={() => onCopyPrep()}
 >
 Copy prep link
 </button>
 </dd>
 <dt className={RAIL_PARAM_DT}>Expires</dt>
 <dd className={RAIL_PARAM_DD}>{session.expires_at ? fmtDateLong(session.expires_at) : '—'}</dd>
 <dt className={RAIL_PARAM_DT}>Completed</dt>
 <dd className={RAIL_PARAM_DD}>{session.completed_at ? fmtDateLong(session.completed_at) : '—'}</dd>
 </dl>
 </div>
 </div>

 {applicationSnapshot ? (
 <div className="ggj-param-group overflow-hidden">
 <div className="ggj-profile-section-h !text-[11px]">Pipeline application</div>
 <div className="ggj-profile-section-body !px-2.5 !py-2">
 <dl className="ggj-attr-grid gap-y-1.5">
 <dt className={RAIL_PARAM_DT}>Stage</dt>
 <dd className={RAIL_PARAM_DD}>{PIPELINE_STATUS_LABEL[applicationSnapshot.status]}</dd>
 <dt className={RAIL_PARAM_DT}>Reference</dt>
 <dd className={RAIL_PARAM_DD}>
 {buildApplicationRefDisplay(applicationSnapshot.id, applicationSnapshot.client_id)}
 </dd>
 <dt className={RAIL_PARAM_DT}>Linked round</dt>
 <dd className={RAIL_PARAM_DD}>
 {session.pipeline_interview_event?.scheduled_at
 ? fmtDateLong(session.pipeline_interview_event.scheduled_at)
 : session.pipeline_interview_event_id
 ? 'Linked (not scheduled)'
 : '—'}
 </dd>
 </dl>
 </div>
 </div>
 ) : null}

 <div className="ggj-param-group overflow-hidden">
 <div className="ggj-profile-section-h !text-[11px]">System / Audit</div>
 <div className="ggj-profile-section-body !px-2.5 !py-2">
 <dl className="ggj-attr-grid gap-y-1.5">
 <dt className={RAIL_PARAM_DT}>Org ID</dt>
 <dd className={RAIL_PARAM_DD}>
 <code className="text-[10px] break-all">{orgId ?? '—'}</code>
 </dd>
 <dt className={RAIL_PARAM_DT}>Created by</dt>
 <dd className={RAIL_PARAM_DD}>
 {session.initiator?.display_name?.trim() ||
 session.initiator?.email ||
 (session.created_by_org_user_id ? (
 <code className="text-[10px] break-all">{session.created_by_org_user_id}</code>
 ) : (
 '—'
 ))}
 </dd>
 <dt className={RAIL_PARAM_DT}>Created</dt>
 <dd className={RAIL_PARAM_DD}>{fmtDateLong(session.created_at)}</dd>
 <dt className={RAIL_PARAM_DT}>Last modified</dt>
 <dd className={RAIL_PARAM_DD}>{fmtDateLong(session.updated_at)}</dd>
 <dt className={RAIL_PARAM_DT}>Modified by</dt>
 <dd className={`${RAIL_PARAM_DD} vscode-text-secondary`}>Not stored</dd>
 </dl>
 </div>
 </div>

 <div className="flex flex-col gap-1.5 pt-0.5">
 <button type="button" className="btn-secondary ggj-text-meta w-full justify-center" onClick={() => onParametersTab()}>
 Open Parameters tab
 </button>
 <button type="button" className="btn-primary ggj-text-meta w-full justify-center font-semibold" onClick={() => onEmailTab()}>
 Email tab (tracked)
 </button>
 </div>
 </div>
 );
}

export function AiInterviewSessionDetailPage() {
 const { sessionId } = useParams<{ sessionId: string }>();
 const [searchParams, setSearchParams] = useSearchParams();
 const experienceQuery = searchParams.get('experience');
 const { user } = useAuth();
 const perms = usePermissions();
 const orgId = user?.orgId;
 const orgName = user?.orgName ?? null;

 const tabFromUrl = parseAiInterviewSessionTab(searchParams.get('tab'));
 const [activeTab, setActiveTab] = useState<AiInterviewSessionTabId>(tabFromUrl ?? 'candidate');

 /** Review → Summary/Transcript/Recording inner tab (T-12 §12 E). */
 const parseSummaryTab = (raw: string | null): InterviewSummaryTabId =>
 raw === 'transcript' || raw === 'recording' ? raw : 'summary';
 const [summaryTab, setSummaryTab] = useState<InterviewSummaryTabId>(
 parseSummaryTab(searchParams.get('rtab')),
 );
 const handleSummaryTabChange = useCallback(
 (next: InterviewSummaryTabId) => {
 setSummaryTab(next);
 setSearchParams(
 (prev) => {
 const n = new URLSearchParams(prev);
 if (next === 'summary') n.delete('rtab');
 else n.set('rtab', next);
 return n;
 },
 { replace: true },
 );
 },
 [setSearchParams],
 );
 useEffect(() => {
 setSummaryTab(parseSummaryTab(searchParams.get('rtab')));
 }, [searchParams]);

 const setTab = useCallback(
 (t: AiInterviewSessionTabId) => {
 setActiveTab(t);
 setSearchParams(
 (prev) => {
 const next = new URLSearchParams(prev);
 if (t === 'candidate') next.delete('tab');
 else next.set('tab', t);
 return next;
 },
 { replace: true }
 );
 },
 [setSearchParams]
 );

 useEffect(() => {
 const t = parseAiInterviewSessionTab(searchParams.get('tab'));
 if (t) setActiveTab(t);
 }, [searchParams]);

 useEffect(() => {
 if (activeTab !== 'email') setEmailComposeOpen(false);
 }, [activeTab]);

 const [loading, setLoading] = useState(true);
 const [session, setSession] = useState<AiInterviewSessionHubDetailRow | null>(null);
 const [candidate, setCandidate] = useState<Candidate | null>(null);
 const [threads, setThreads] = useState<EmailThread[]>([]);
 const [events, setEvents] = useState<InterviewEvent[]>([]);
 const [threadsLoading, setThreadsLoading] = useState(false);
 const [eventsLoading, setEventsLoading] = useState(false);
 const [copyMsg, setCopyMsg] = useState<string | null>(null);
 const [applicationSnapshot, setApplicationSnapshot] = useState<Application | null>(null);
 const [applicationLoading, setApplicationLoading] = useState(false);
 /** All applications for this candidate — needed for AI interview scheduling rules on the AI interviews tab. */
 const [candidateApplications, setCandidateApplications] = useState<Application[]>([]);
 /** Same candidate list as the panel — used to show other sessions in the AI interviews tab rail only. */
 const [otherAiSessionsForRail, setOtherAiSessionsForRail] = useState<AiInterviewSessionWithArtifacts[]>([]);
 const [emailComposeOpen, setEmailComposeOpen] = useState(false);
 const [calendarMonthAnchor, setCalendarMonthAnchor] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
 const [selectedCalendarEventId, setSelectedCalendarEventId] = useState<string | null>(null);
 const [aiScheduleWizardOpen, setAiScheduleWizardOpen] = useState(false);
 const [aiScheduleWizardDayKey, setAiScheduleWizardDayKey] = useState<string | null>(null);
 const [showExperienceFeedback, setShowExperienceFeedback] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [pageToast, setPageToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  /** Increment to trigger session refetch without a route change (used by template regeneration). */
  const [refreshTick, setRefreshTick] = useState(0);

  const pushPageToast = useCallback((kind: 'ok' | 'err', text: string) => {
    setPageToast({ kind, text });
  }, []);

  /** C-24 §12 — template selector in Summary tab. */
  const handleTemplateChange = useCallback(async (templateLabel: string) => {
    if (!orgId || !sessionId) return;
    pushPageToast('ok', `Template applied — refetching…`);
    try {
      await aiInterviewSessionService.regenerateWithTemplate(orgId, sessionId, templateLabel);
    } catch {
      // Non-fatal — the stub may fail in older envs without the column; still trigger refetch.
    }
    setRefreshTick((t) => t + 1);
  }, [orgId, sessionId, pushPageToast]);

 useEffect(() => {
 if (!pageToast) return;
 const t = window.setTimeout(() => setPageToast(null), 4200);
 return () => window.clearTimeout(t);
 }, [pageToast]);

 useEffect(() => {
 if (!session || loading) return;
 if (searchParams.get('tab')) return;
 const done =
 session.status === 'completed' || hasMeaningfulAssessment(session.assessed_at, session.assessment_scores);
 if (done) setTab('review');
 }, [session, loading, searchParams, setTab]);

 useEffect(() => {
 if (!orgId || !sessionId) return;
 let cancelled = false;
 setLoading(true);
 setSession(null);
 setCandidate(null);
 (async () => {
 try {
 const s = await aiInterviewSessionService.getByIdForOrg(orgId, sessionId);
 if (cancelled) return;
 if (!s) {
 setSession(null);
 return;
 }
 setSession(s);
 const cand = await candidateService.getById(s.candidate_id);
 if (cancelled) return;
 setCandidate(cand);
 } finally {
 if (!cancelled) setLoading(false);
 }
 })();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, sessionId, refreshTick]);

 useEffect(() => {
 if (!orgId || !candidate?.id) {
 setOtherAiSessionsForRail([]);
 return;
 }
 let cancelled = false;
 aiInterviewSessionService
 .listForCandidate(orgId, candidate.id, 20)
 .then((list) => {
 if (cancelled) return;
 setOtherAiSessionsForRail(list.filter((row) => row.id !== sessionId));
 })
 .catch(() => {
 if (!cancelled) setOtherAiSessionsForRail([]);
 });
 return () => {
 cancelled = true;
 };
 }, [orgId, candidate?.id, sessionId]);

 const canViewSession = useMemo(() => {
 if (!session) return false;
 if (perms.isOwner || perms.isAdmin) return true;
 const uid = user?.orgUserId;
 const creator = session.created_by_org_user_id;
 return creator == null || creator === uid;
 }, [session, perms.isOwner, perms.isAdmin, user?.orgUserId]);

 const applicationId = session?.application_id ?? null;

 /** Application row for AI interviews tab Context rail (matches CandidateAiInterviewsPanel). */
 const aiInterviewsTabContextApp = useMemo(() => {
 if (!session?.application_id) return null;
 if (applicationSnapshot?.id === session.application_id) return applicationSnapshot;
 return candidateApplications.find((a) => a.id === session.application_id) ?? null;
 }, [session, applicationSnapshot, candidateApplications]);

 const emailThreadDisplayList = useMemo(() => {
 const roots = threads.filter((t) => !t.in_reply_to_thread_id);
 return roots.length > 0 ? roots : threads;
 }, [threads]);

 const emailReplyCountByParent = useMemo(() => {
 const m: Record<string, number> = {};
 for (const t of threads) {
 if (t.in_reply_to_thread_id) {
 const pid = t.in_reply_to_thread_id;
 m[pid] = (m[pid] ?? 0) + 1;
 }
 }
 return m;
 }, [threads]);

 const monthCalendarEvents: MonthCalendarEvent[] = useMemo(() => {
 const start = new Date(calendarMonthAnchor.getFullYear(), calendarMonthAnchor.getMonth(), 1);
 const end = new Date(calendarMonthAnchor.getFullYear(), calendarMonthAnchor.getMonth() + 1, 0, 23, 59, 59, 999);
 return events
 .filter((r) => r.scheduled_at)
 .filter((r) => {
 const t = new Date(r.scheduled_at!);
 return t >= start && t <= end;
 })
 .map((r) => ({ id: r.id, scheduled_at: r.scheduled_at!, label: `R${r.round}` }));
 }, [events, calendarMonthAnchor]);

 const calendarQuickLinks = useMemo(() => {
 if (!session) return [] as { key: string; to: string; label: string; external?: boolean }[];
 const prep = interviewPrepUrl(session.access_token);
 const cid = session.candidate_id;
 const rows: { key: string; to: string; label: string; external?: boolean }[] = [
 { key: 'prep', to: prep, label: 'Prep workspace', external: true },
 ];
 if (applicationId) {
 rows.push({ key: 'appint', to: `/applications/${applicationId}?tab=interviews`, label: 'Application · Interviews' });
 rows.push({
 key: 'iwork',
 to: `/interviews/candidate/${cid}?applicationId=${applicationId}&tab=interview`,
 label: 'Interviews workspace',
 });
 }
 rows.push({ key: 'hub', to: '/interviews', label: 'All interviews' });
 rows.push({ key: 'aihub', to: '/ai-interviews', label: 'AI interviews hub' });
 rows.push({ key: 'cand', to: `/candidates/${cid}`, label: 'Candidate profile' });
 return rows;
 }, [applicationId, session]);

 const refreshThreadsAndEvents = useCallback(async () => {
 if (!applicationId) {
 setThreads([]);
 setEvents([]);
 return;
 }
 setThreadsLoading(true);
 setEventsLoading(true);
 try {
 const [t, ev] = await Promise.all([
 emailService.listByApplication(applicationId),
 interviewEventService.listByApplication(applicationId),
 ]);
 setThreads(t);
 setEvents(ev);
 } catch {
 setThreads([]);
 setEvents([]);
 } finally {
 setThreadsLoading(false);
 setEventsLoading(false);
 }
 }, [applicationId]);

 useEffect(() => {
 if (!applicationId) {
 setThreads([]);
 setEvents([]);
 setApplicationSnapshot(null);
 return;
 }
 void refreshThreadsAndEvents();
 }, [applicationId, refreshThreadsAndEvents]);

 useEffect(() => {
 if (!orgId || !session?.candidate_id) return;
 let cancelled = false;
 applicationService
 .list(orgId, { candidateId: session.candidate_id })
 .then((apps) => {
 if (!cancelled) setCandidateApplications(apps);
 })
 .catch(() => {
 if (!cancelled) setCandidateApplications([]);
 });
 return () => {
 cancelled = true;
 };
 }, [orgId, session?.candidate_id]);

 const reloadSession = useCallback(async () => {
 if (!orgId || !sessionId) return;
 try {
 const s = await aiInterviewSessionService.getByIdForOrg(orgId, sessionId);
 if (s) setSession(s);
 } catch {
 /* ignore */
 }
 }, [orgId, sessionId]);

 useEffect(() => {
 if (!sessionId || !session || loading) return;
 if (experienceQuery !== '1') return;
 const t = sessionStorage.getItem(`ggj_interview_feedback_token_${sessionId}`);
 if (!t) {
 setSearchParams(
 (p) => {
 const n = new URLSearchParams(p);
 n.delete('experience');
 return n;
 },
 { replace: true },
 );
 return;
 }
 setShowExperienceFeedback(true);
 }, [sessionId, session, loading, experienceQuery, setSearchParams]);

 const closeExperienceFeedback = useCallback(() => {
 if (sessionId) sessionStorage.removeItem(`ggj_interview_feedback_token_${sessionId}`);
 setShowExperienceFeedback(false);
 setSearchParams(
 (p) => {
 const n = new URLSearchParams(p);
 n.delete('experience');
 return n;
 },
 { replace: true },
 );
 }, [sessionId, setSearchParams]);

 const submitExperienceFromDetail = useCallback(
 async (rating: number) => {
 if (!sessionId) throw new Error('Missing session.');
 const t = sessionStorage.getItem(`ggj_interview_feedback_token_${sessionId}`);
 if (!t) throw new Error('Session expired. Open the interview link again to rate.');
 await postInterviewExperienceFeedback(t, rating);
 sessionStorage.removeItem(`ggj_interview_feedback_token_${sessionId}`);
 setShowExperienceFeedback(false);
 setSearchParams(
 (p) => {
 const n = new URLSearchParams(p);
 n.delete('experience');
 return n;
 },
 { replace: true },
 );
 await reloadSession();
 },
 [sessionId, setSearchParams, reloadSession],
 );

 useEffect(() => {
 if (!applicationId) {
 setApplicationSnapshot(null);
 return;
 }
 let cancelled = false;
 setApplicationLoading(true);
 applicationService
 .getById(applicationId)
 .then((app) => {
 if (!cancelled) setApplicationSnapshot(app);
 })
 .catch(() => {
 if (!cancelled) setApplicationSnapshot(null);
 })
 .finally(() => {
 if (!cancelled) setApplicationLoading(false);
 });
 return () => {
 cancelled = true;
 };
 }, [applicationId]);

 useEffect(() => {
 if (session?.pipeline_interview_event_id) {
 setSelectedCalendarEventId(session.pipeline_interview_event_id);
 }
 }, [session?.pipeline_interview_event_id]);

 async function copyPrepLink() {
 if (!session) return;
 const url = interviewPrepUrl(session.access_token);
 try {
 await navigator.clipboard.writeText(url);
 setCopyMsg('Copied prep link.');
 window.setTimeout(() => setCopyMsg(null), 2200);
 } catch {
 setCopyMsg('Copy failed.');
 }
 }

 const candidateName = session?.candidate?.full_name?.trim() || candidate?.full_name || 'Candidate';

 const aiEmailTemplateOverrides = useMemo((): Partial<TemplateVars> => {
 if (typeof window === 'undefined' || !session) return {};
 return {
 PREP_URL: `${window.location.origin}/interview/prep/${session.access_token}`,
 SESSION_TYPE_LABEL: ROUND_LABEL[session.session_type] ?? session.session_type,
 INTERVIEW_ROUND: ROUND_LABEL[session.session_type] ?? session.session_type,
 };
 }, [session?.access_token, session?.session_type]);

 const openAiScheduleWizardForDay = useCallback(
 (day: Date) => {
 if (!applicationSnapshot || !applicationId) return;
 setAiScheduleWizardDayKey(toLocalDayKey(day));
 setAiScheduleWizardOpen(true);
 },
 [applicationSnapshot, applicationId]
 );

 const selectedCalEvent = useMemo(
 () => (selectedCalendarEventId ? events.find((e) => e.id === selectedCalendarEventId) ?? null : null),
 [events, selectedCalendarEventId]
 );

 const reviewAttempts = useMemo(() => {
 if (!session) return [];
 return listInterviewAttempts(session as AiInterviewSessionWithArtifacts);
 }, [session]);

 const reviewDecisionsEnabled = useMemo(() => {
 if (!perms.canUpdateApplicationStatus || !applicationId || !applicationSnapshot || !session) return false;
 if (session.status !== 'completed') return false;
 const terminal = ['rejected', 'placed', 'closed', 'offer_rejected', 'no_show'] as const;
 return !terminal.includes(applicationSnapshot.status as (typeof terminal)[number]);
 }, [perms.canUpdateApplicationStatus, applicationId, applicationSnapshot, session]);

 const onReviewApprove = useCallback(async () => {
 if (!applicationId || !applicationSnapshot) return;
 setReviewBusy(true);
 try {
 await applicationService.updateStatus(
 applicationId,
 'shortlisted',
 'AI interview approved (reviewer).',
 null,
 null,
 { expectedUpdatedAt: applicationSnapshot.updated_at }
 );
 const app = await applicationService.getById(applicationId);
 if (app) setApplicationSnapshot(app);
 pushPageToast('ok', 'Application moved to Shortlisted.');
 await reloadSession();
 } catch (e) {
 pushPageToast('err', e instanceof Error ? e.message : 'Could not update application.');
 } finally {
 setReviewBusy(false);
 }
 }, [applicationId, applicationSnapshot, pushPageToast, reloadSession]);

 const onReviewReject = useCallback(async () => {
 if (!applicationId || !applicationSnapshot) return;
 setReviewBusy(true);
 try {
 await applicationService.updateStatus(
 applicationId,
 'rejected',
 'AI interview rejected (reviewer).',
 null,
 null,
 { expectedUpdatedAt: applicationSnapshot.updated_at }
 );
 const app = await applicationService.getById(applicationId);
 if (app) setApplicationSnapshot(app);
 pushPageToast('ok', 'Application marked Rejected.');
 await reloadSession();
 } catch (e) {
 pushPageToast('err', e instanceof Error ? e.message : 'Could not update application.');
 } finally {
 setReviewBusy(false);
 }
 }, [applicationId, applicationSnapshot, pushPageToast, reloadSession]);

 const onReviewEscalate = useCallback(async () => {
 if (!applicationId || !applicationSnapshot) return;
 setReviewBusy(true);
 try {
 await applicationService.updateStatus(
 applicationId,
 'on_hold',
 'Escalated after AI interview review for further decision.',
 null,
 null,
 { expectedUpdatedAt: applicationSnapshot.updated_at }
 );
 const app = await applicationService.getById(applicationId);
 if (app) setApplicationSnapshot(app);
 pushPageToast('ok', 'Application moved to On hold (escalated).');
 await reloadSession();
 } catch (e) {
 pushPageToast('err', e instanceof Error ? e.message : 'Could not update application.');
 } finally {
 setReviewBusy(false);
 }
 }, [applicationId, applicationSnapshot, pushPageToast, reloadSession]);

 if (!orgId) return null;

 if (loading) return <FullPageSpinner />;

 if (!session || !canViewSession) {
 return (
 <div className="w-full min-w-0 space-y-3">
 <p className="ggj-text-body vscode-text-secondary">
 This AI interview session was not found or you do not have access.
 </p>
 <Link to="/ai-interviews" className="ggj-text-body text-[var(--vscode-text-link)] hover:underline">
 ← Back to AI interviews
 </Link>
 </div>
 );
 }

 if (!candidate) {
 return (
 <div className="w-full min-w-0 space-y-3">
 <p className="ggj-text-body vscode-text-secondary">Could not load candidate for this session.</p>
 <Link to="/ai-interviews" className="ggj-text-body text-[var(--vscode-text-link)] hover:underline">
 ← Back to AI interviews
 </Link>
 </div>
 );
 }

 const headerChipKind = resolveReviewerSessionStatus(session.status, session.assessed_at, session.assessment_scores);
 const headerScoreLine = reviewerListScoreDisplay(session.assessment_scores, session.assessed_at);

 return (
 <div className="mx-auto w-full min-w-0 max-w-none">
 <AiInterviewExperienceModal
 open={showExperienceFeedback}
 onClose={closeExperienceFeedback}
 onSubmit={(rating) => submitExperienceFromDetail(rating)}
 />
 <RecordHeader
 avatarInitials={recordInitials(candidateName)}
 title={candidateName}
 meta={[
 `AI interview · ${ROUND_LABEL[session.session_type] ?? session.session_type}`,
 `Session ${formatDateTimeGmt(reviewerSessionDateIso(session))}`,
 headerScoreLine !== '—' ? `Score ${headerScoreLine}` : null,
 ]
 .filter(Boolean)
 .join(' · ')}
 breadcrumb={[
 { label: 'AI interviews', href: '/ai-interviews' },
 { label: candidateName },
 ]}
 actionsEnd={
 <StatusChip
 label={reviewerStatusChipLabel(headerChipKind)}
 dotColor={statusDotColorFromHint(reviewerStatusChipLabel(headerChipKind))}
 />
 }
 />

 {pageToast ? (
 <p
 className={`ggj-text-body rounded-lg border px-3 py-2 mb-4 ${
 pageToast.kind === 'ok'
 ? 'border-emerald-500/30 bg-emerald-950/25 text-emerald-700 dark:text-emerald-300'
 : 'border-red-500/30 bg-red-950/30 text-red-700 dark:text-red-300'
 }`}
 role="status"
 >
 {pageToast.text}
 </p>
 ) : null}

 <nav className="ggj-cand-tablist mb-4" aria-label="AI interview session">
 {AI_INTERVIEW_SESSION_TABS.map((tid) => {
 const labels: Record<AiInterviewSessionTabId, string> = {
 review: 'Review',
 candidate: 'Profile',
 email: 'Email',
 calendar: 'Calendar',
 'ai-interviews': 'AI interviews',
 parameters: 'Parameters',
 };
 const active = activeTab === tid;
 return (
 <button
 key={tid}
 type="button"
 role="tab"
 aria-selected={active}
 onClick={() => setTab(tid)}
 className={`ggj-cand-tab ${active ? 'ggj-cand-tab--active' : ''}`}
 >
 {labels[tid]}
 </button>
 );
 })}
 </nav>

 {activeTab === 'review' && (
 <InterviewReviewPanel
 session={session}
 candidate={candidate}
 candidateName={candidateName}
 applicationId={applicationId}
 applicationSnapshot={applicationSnapshot}
 reviewDecisionsEnabled={reviewDecisionsEnabled}
 reviewBusy={reviewBusy}
 onReviewApprove={onReviewApprove}
 onReviewReject={onReviewReject}
 onReviewEscalate={onReviewEscalate}
          summaryTab={summaryTab}
          onSummaryTabChange={handleSummaryTabChange}
          onTemplateChange={handleTemplateChange}
          companyName={orgName ?? ''}
          prepUrl={interviewPrepUrl(session.access_token)}
 />
 )}

 {activeTab === 'candidate' && (
 <div className="space-y-3 min-w-0">
 <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(300px,28%)] gap-3 lg:gap-5 items-start">
 <div className="space-y-3 min-w-0 order-1">
 <div className="ggj-param-group overflow-hidden">
 <div className="ggj-profile-toolbar flex-wrap justify-between gap-y-2">
 <span className="ggj-text-meta font-semibold uppercase tracking-wider vscode-text-tertiary">Profile</span>
 <Link
 to={`/candidates/${candidate.id}`}
 target="_blank"
 rel="noopener noreferrer"
 className="ggj-text-meta text-[var(--vscode-text-link)] hover:underline font-medium shrink-0 inline-flex items-center gap-1"
 >
 <ExternalLink className="w-3.5 h-3.5" aria-hidden />
 Open full profile
 </Link>
 </div>
 <CandidateProfileSummary candidate={candidate} />
 {user?.orgId && user?.id ? (
 <CandidateForm
 key={`${candidate.id}-${candidate.updated_at ?? ''}`}
 mode="edit"
 candidate={candidate}
 readOnly
 orgId={user.orgId}
 userId={user.id}
 onSave={(updated) => setCandidate(updated)}
 formId="ai-interview-session-candidate-ro"
 />
 ) : null}
 </div>

 <div className="ggj-param-group overflow-hidden p-4 space-y-3">
 <div className="ggj-profile-toolbar flex-wrap justify-between gap-y-2">
 <span className="ggj-text-meta font-semibold uppercase tracking-wider vscode-text-tertiary">
 AI interview session
 </span>
 </div>
 <p className="ggj-text-meta vscode-text-secondary leading-relaxed">
 Scheduling and prep for this round. Candidate profile fields above mirror{' '}
 <strong className="text-[var(--vscode-foreground)]">Data → Candidates → Profile</strong> (read-only here).
 </p>
 <dl className="ggj-attr-grid ggj-text-body">
 <dt className="vscode-text-secondary pr-2">Round</dt>
 <dd className="vscode-text-primary capitalize">{ROUND_LABEL[session.session_type] ?? session.session_type}</dd>
 <dt className="vscode-text-secondary pr-2">Language</dt>
 <dd className="vscode-text-primary font-mono ggj-text-meta">{session.language}</dd>
 <dt className="vscode-text-secondary pr-2">Session status</dt>
 <dd className="vscode-text-primary capitalize">{session.status}</dd>
 <dt className="vscode-text-secondary pr-2">Prep link</dt>
 <dd className="min-w-0">
 <div className="flex flex-wrap gap-2 items-center">
 <button
 type="button"
 className="btn-secondary ggj-text-meta inline-flex items-center gap-1"
 onClick={() => void copyPrepLink()}
 >
 <Copy className="w-3 h-3" aria-hidden />
 Copy link
 </button>
 <a
 href={interviewPrepUrl(session.access_token)}
 target="_blank"
 rel="noopener noreferrer"
 className="btn-secondary ggj-text-meta inline-flex items-center gap-1"
 >
 <ExternalLink className="w-3 h-3" aria-hidden />
 Open prep
 </a>
 {copyMsg && <span className="ggj-text-meta text-emerald-600 dark:text-emerald-400">{copyMsg}</span>}
 </div>
 </dd>
 <dt className="vscode-text-secondary pr-2">Created</dt>
 <dd className="vscode-text-primary whitespace-nowrap">{formatDateTimeGmt(session.created_at)}</dd>
 <dt className="vscode-text-secondary pr-2">Link expires</dt>
 <dd className="vscode-text-primary whitespace-nowrap">
 {session.expires_at ? formatDateTimeGmt(session.expires_at) : '—'}
 </dd>
 <dt className="vscode-text-secondary pr-2">AI interview completed</dt>
 <dd className="vscode-text-primary whitespace-nowrap">
 {session.completed_at ? formatDateTimeGmt(session.completed_at) : '—'}
 </dd>
 {session.initiator && (
 <>
 <dt className="vscode-text-secondary pr-2">Scheduled by</dt>
 <dd className="vscode-text-primary font-medium">
 {session.initiator.display_name?.trim() || session.initiator.email || '—'}
 </dd>
 </>
 )}
 {session.pipeline_interview_event && (
 <>
 <dt className="vscode-text-secondary pr-2">Linked pipeline slot</dt>
 <dd className="vscode-text-primary min-w-0">
 <span className="capitalize">{eventStatusLabel(session.pipeline_interview_event.status)}</span>
 {session.pipeline_interview_event.scheduled_at ? (
 <span className="vscode-text-secondary">
 {' '}
 · {formatDateTimeGmt(session.pipeline_interview_event.scheduled_at)}
 </span>
 ) : null}
 </dd>
 </>
 )}
 </dl>
 </div>
 </div>

 <aside className="ggj-rail-aside space-y-2 lg:sticky lg:top-4 self-start order-2 min-w-0 max-w-full">
 <CandidateCollapsiblePanel
 storageKey={`ai-int-cand-${sessionId}-actions`}
 title="Quick actions"
 defaultOpen
 >
 <p className="ggj-text-meta vscode-text-secondary mb-2">
 Tracked email and calendar use the same application as this AI interview when linked.
 </p>
 <div className="flex flex-col gap-2">
 <button
 type="button"
 className="btn-secondary ggj-text-meta w-full justify-center inline-flex items-center gap-1.5 font-semibold"
 onClick={() => void copyPrepLink()}
 >
 <Copy className="w-3.5 h-3.5" aria-hidden />
 Copy prep link
 </button>
 <a
 href={interviewPrepUrl(session.access_token)}
 target="_blank"
 rel="noopener noreferrer"
 className="btn-primary ggj-text-meta w-full justify-center inline-flex items-center gap-1.5 font-semibold"
 >
 <MonitorPlay className="w-3.5 h-3.5" aria-hidden />
 Open prep (candidate view)
 </a>
 {applicationId ? (
 <>
 <button
 type="button"
 className="btn-primary ggj-text-meta w-full justify-center font-semibold"
 onClick={() => {
 setTab('email');
 setEmailComposeOpen(true);
 }}
 >
 Compose tracked email
 </button>
 <button
 type="button"
 className="btn-secondary ggj-text-meta w-full justify-center inline-flex items-center gap-1.5 font-semibold"
 onClick={() => setTab('calendar')}
 >
 <Calendar className="w-3.5 h-3.5" aria-hidden />
 Calendar &amp; booking
 </button>
 </>
 ) : null}
 <button
 type="button"
 className="btn-secondary ggj-text-meta w-full justify-center inline-flex items-center gap-1.5 font-semibold"
 onClick={() => setTab('parameters')}
 >
 <Settings2 className="w-3.5 h-3.5" aria-hidden />
 Session parameters
 </button>
 </div>
 </CandidateCollapsiblePanel>

 <CandidateCollapsiblePanel
 storageKey={`ai-int-cand-${sessionId}-pipeline`}
 title="Pipeline application"
 defaultOpen
 >
 {applicationLoading ? (
 <p className="ggj-text-meta vscode-text-tertiary">Loading application…</p>
 ) : applicationSnapshot ? (
 <div className="space-y-2 ggj-text-body min-w-0">
 <dl className="space-y-1.5">
 <div className="flex justify-between gap-2">
 <span className="ggj-text-meta vscode-text-tertiary shrink-0">Job</span>
 <span className="ggj-text-meta font-medium vscode-text-primary text-right min-w-0 break-words">
 {applicationSnapshot.job?.title ?? '—'}
 </span>
 </div>
 <div className="flex justify-between gap-2">
 <span className="ggj-text-meta vscode-text-tertiary shrink-0">Client</span>
 <span className="ggj-text-meta vscode-text-primary text-right min-w-0 break-words">
 {applicationSnapshot.client?.name ?? '—'}
 </span>
 </div>
 <div className="flex justify-between gap-2">
 <span className="ggj-text-meta vscode-text-tertiary shrink-0">Pipeline status</span>
 <span className="ggj-text-meta font-medium vscode-text-primary capitalize text-right">
 {applicationSnapshot.status}
 </span>
 </div>
 </dl>
 {applicationId ? (
 <Link
 to={`/applications/${applicationId}`}
 target="_blank"
 rel="noopener noreferrer"
 className="inline-flex items-center gap-1 ggj-text-meta font-medium text-[var(--vscode-text-link)] hover:underline"
 >
 Open application
 <ExternalLink className="w-3 h-3" aria-hidden />
 </Link>
 ) : null}
 </div>
 ) : applicationId ? (
 <p className="ggj-text-meta vscode-text-tertiary">Could not load application details.</p>
 ) : (
 <p className="ggj-text-meta vscode-text-tertiary">No application is linked to this AI interview session.</p>
 )}
 </CandidateCollapsiblePanel>

 <CandidateCollapsiblePanel
 storageKey={`ai-int-cand-${sessionId}-related`}
 title="Related"
 defaultOpen
 >
 <div className="flex flex-col gap-2 ggj-text-meta">
 <Link
 to={`/candidates/${candidate.id}`}
 target="_blank"
 rel="noopener noreferrer"
 className="text-[var(--vscode-text-link)] hover:underline inline-flex items-center gap-1 font-medium"
 >
 <User className="w-3.5 h-3.5" aria-hidden />
 Candidate profile (Data → Candidates)
 </Link>
 <Link
 to="/ai-interviews"
 className="text-[var(--vscode-text-link)] hover:underline font-medium"
 >
 AI interviews hub (org list)
 </Link>
 <Link
 to="/emails"
 target="_blank"
 rel="noopener noreferrer"
 className="text-[var(--vscode-text-link)] hover:underline inline-flex items-center gap-1 font-medium"
 >
 <Mail className="w-3.5 h-3.5" aria-hidden />
 Email inbox
 </Link>
 </div>
 </CandidateCollapsiblePanel>
 </aside>
 </div>
 </div>
 )}

 {activeTab === 'email' && (
 <div className="min-w-0 space-y-0">
 {!applicationId ? (
 <div className="ggj-param-group overflow-hidden p-4 space-y-2">
 <h2 className="ggj-text-body font-semibold uppercase tracking-wide vscode-text-tertiary flex items-center gap-2">
 <Mail className="w-4 h-4 shrink-0" aria-hidden />
 Email &amp; follow-up
 </h2>
 <p className="ggj-text-body text-amber-700 dark:text-amber-200/90">
 No application is linked to this AI interview session yet — tracked email needs the same application scope as
 Pipeline → Emails.
 </p>
 </div>
 ) : emailComposeOpen ? (
 <EmailComposeView
 embedded
 applicationId={applicationId}
 returnTo={`/ai-interviews/${sessionId}?tab=email`}
 defaultRecipient="candidate"
 initialTemplateSelect="b:ai_interview_prep_invite_candidate"
 templateVarOverrides={aiEmailTemplateOverrides}
 onCancel={() => setEmailComposeOpen(false)}
 onSent={() => {
 void refreshThreadsAndEvents();
 setEmailComposeOpen(false);
 }}
 />
 ) : (
 <>
 <nav className="flex flex-wrap items-center gap-x-1 gap-y-1 ggj-text-meta mb-3" aria-label="Pipeline email context">
 <Link to="/applications" className="text-[var(--vscode-text-link)] hover:underline font-medium">
 Pipeline
 </Link>
 <ChevronRight className="w-3.5 h-3.5 vscode-text-tertiary shrink-0" aria-hidden />
 <Link to="/emails" className="text-[var(--vscode-text-link)] hover:underline font-medium">
 Email activity
 </Link>
 <ChevronRight className="w-3.5 h-3.5 vscode-text-tertiary shrink-0" aria-hidden />
 <span className="vscode-text-primary min-w-0 break-words">
 {applicationLoading ? (
 <span className="vscode-text-tertiary">Loading application…</span>
 ) : applicationSnapshot ? (
 <>
 {PIPELINE_STATUS_LABEL[applicationSnapshot.status]} — {candidate.full_name} ·{' '}
 {applicationSnapshot.job?.title ?? '—'} ·{' '}
 <span className="font-mono tabular-nums">
 {buildApplicationRefDisplay(applicationSnapshot.id, applicationSnapshot.client_id)}
 </span>
 </>
 ) : (
 <span className="vscode-text-tertiary">Application</span>
 )}
 </span>
 <ChevronRight className="w-3.5 h-3.5 vscode-text-tertiary shrink-0" aria-hidden />
 <span className="font-semibold vscode-text-primary">Email</span>
 </nav>

 <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
 <div className="min-w-0 space-y-4">
 {threadsLoading ? (
 <div className="flex justify-center py-12">
 <Loader2 className="w-8 h-8 animate-spin vscode-text-tertiary" aria-hidden />
 </div>
 ) : (
 <>
 <SessionEmailAttrGroup title="Email Threads">
 {threads.length === 0 ? (
 <div className="py-6 text-center space-y-2">
 <MailOpen className="w-9 h-9 vscode-text-tertiary mx-auto opacity-80" aria-hidden />
 <p className="vscode-text-secondary ggj-text-body">No emails for this application yet.</p>
 <p className="ggj-text-meta">
 <button
 type="button"
 className="text-[var(--vscode-text-link)] hover:underline font-medium"
 onClick={() => setEmailComposeOpen(true)}
 >
 Compose email
 </button>
 </p>
 </div>
 ) : (
 <dl className="ggj-attr-grid ggj-text-body">
 <dt className="py-2 ggj-text-meta vscode-text-tertiary font-medium leading-5 pr-2">Threads</dt>
 <dd className="py-2 ggj-text-body vscode-text-primary leading-5">
 <div className="space-y-1.5">
 {emailThreadDisplayList.map((thread) => {
 const replyN = emailReplyCountByParent[thread.id] ?? 0;
 const isResponded = !!thread.cta_response;
 const isAwaiting = !isResponded && !!thread.sent_at;
 return (
 <Link
 key={thread.id}
 to={`/emails/thread/${thread.id}`}
 target="_blank"
 rel="noopener noreferrer"
 className="flex items-center gap-2 text-brand-400 hover:text-brand-300 hover:underline ggj-text-body text-left group w-full"
 >
 <span className="truncate">{thread.subject?.trim() || '(No subject)'}</span>
 {isAwaiting ? (
 <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0 animate-pulse" aria-hidden />
 ) : null}
 {isResponded ? <CheckCircle className="w-3.5 h-3.5 text-green-400 shrink-0" aria-hidden /> : null}
 {replyN > 0 ? (
 <span className="text-[10px] vscode-text-tertiary shrink-0">(+{replyN})</span>
 ) : null}
 </Link>
 );
 })}
 </div>
 </dd>

 <SessionEmailAttrRow label="Candidate">
 {candidate.email ? (
 <a href={`mailto:${candidate.email}`} className="text-brand-400 hover:underline">
 {candidate.email}
 </a>
 ) : (
 '—'
 )}
 </SessionEmailAttrRow>

 <SessionEmailAttrRow label="Client contact">
 {applicationSnapshot?.client?.contact_email ? (
 <a href={`mailto:${applicationSnapshot.client.contact_email}`} className="text-brand-400 hover:underline">
 {applicationSnapshot.client.contact_email}
 </a>
 ) : (
 '—'
 )}
 </SessionEmailAttrRow>

 <SessionEmailAttrRow label="Sent">{threads.length}</SessionEmailAttrRow>

 <SessionEmailAttrRow label="Responded">
 <span className="vscode-text-primary font-medium">{threads.filter((t) => t.cta_response).length}</span>
 </SessionEmailAttrRow>

 <SessionEmailAttrRow label="Awaiting reply">
 <span className="font-medium" style={{ color: 'var(--vscode-text-link)' }}>
 {threads.filter((t) => !t.cta_response && t.sent_at).length}
 </span>
 </SessionEmailAttrRow>

 <SessionEmailAttrRow label="Total opens">
 {threads.reduce((s, t) => s + (t.open_count || 0), 0)}
 </SessionEmailAttrRow>

 <SessionEmailAttrRow label="Last sent">
 {threads.length > 0
 ? formatDateTimeGmt(
 new Date(Math.max(...threads.map((t) => new Date(t.sent_at ?? 0).getTime()))).toISOString()
 )
 : '—'}
 </SessionEmailAttrRow>

 <SessionEmailAttrRow label="Assigned to">
 {applicationSnapshot?.assigned_user?.display_name ??
 applicationSnapshot?.assigned_user?.email ??
 '—'}
 </SessionEmailAttrRow>
 </dl>
 )}
 </SessionEmailAttrGroup>

 {threads.length > 0 ? (
 <SessionEmailAttrGroup title="Email Engagement Summary">
 <dl className="ggj-attr-grid ggj-text-body">
 <SessionEmailAttrRow label="Total threads">{threads.length}</SessionEmailAttrRow>
 <SessionEmailAttrRow label="Responded">
 <span className="vscode-text-primary font-medium">{threads.filter((t) => t.cta_response).length}</span>
 </SessionEmailAttrRow>
 <SessionEmailAttrRow label="Awaiting reply">
 <span className="font-medium" style={{ color: 'var(--vscode-text-link)' }}>
 {threads.filter((t) => !t.cta_response && t.sent_at).length}
 </span>
 </SessionEmailAttrRow>
 <SessionEmailAttrRow label="Total opens">
 {threads.reduce((s, t) => s + (t.open_count || 0), 0)}
 </SessionEmailAttrRow>
 <SessionEmailAttrRow label="With replies">{threads.filter((t) => t.in_reply_to_thread_id).length}</SessionEmailAttrRow>
 <SessionEmailAttrRow label="Response rate">
 {threads.length > 0
 ? `${Math.round((threads.filter((t) => t.cta_response).length / threads.length) * 100)}%`
 : '—'}
 </SessionEmailAttrRow>
 <SessionEmailAttrRow label="Candidate email">
 {candidate.email ? (
 <a href={`mailto:${candidate.email}`} className="text-brand-400 hover:underline">
 {candidate.email}
 </a>
 ) : (
 '—'
 )}
 </SessionEmailAttrRow>
 <SessionEmailAttrRow label="Client contact">
 {applicationSnapshot?.client?.contact_email ? (
 <a href={`mailto:${applicationSnapshot.client.contact_email}`} className="text-brand-400 hover:underline">
 {applicationSnapshot.client.contact_email}
 </a>
 ) : (
 '—'
 )}
 </SessionEmailAttrRow>
 <SessionEmailAttrRow label="First sent">
 {threads.length > 0
 ? formatDateTimeGmt(
 new Date(Math.min(...threads.map((t) => new Date(t.sent_at ?? Date.now()).getTime()))).toISOString()
 )
 : '—'}
 </SessionEmailAttrRow>
 <SessionEmailAttrRow label="Last sent">
 {threads.length > 0
 ? formatDateTimeGmt(
 new Date(Math.max(...threads.map((t) => new Date(t.sent_at ?? 0).getTime()))).toISOString()
 )
 : '—'}
 </SessionEmailAttrRow>
 </dl>
 </SessionEmailAttrGroup>
 ) : null}
 </>
 )}
 </div>

 <aside className="ggj-rail-aside space-y-2 lg:sticky lg:top-4 self-start min-w-0 max-w-full">
 <CandidateCollapsiblePanel
 storageKey={`ai-int-email-${sessionId}-session`}
 title="This AI interview"
 defaultOpen
 >
 <div className="space-y-2 ggj-text-meta">
 <div className="flex justify-between gap-2">
 <span className="vscode-text-tertiary">Round</span>
 <span className="font-medium vscode-text-primary text-right capitalize">
 {ROUND_LABEL[session.session_type] ?? session.session_type}
 </span>
 </div>
 <div className="flex justify-between gap-2">
 <span className="vscode-text-tertiary">Session</span>
 <span className="font-medium vscode-text-primary text-right capitalize">{session.status}</span>
 </div>
 <div className="flex flex-col gap-2 pt-1">
 <button
 type="button"
 className="btn-secondary ggj-text-meta w-full justify-center inline-flex items-center gap-1"
 onClick={() => void copyPrepLink()}
 >
 <Copy className="w-3.5 h-3.5" aria-hidden />
 Copy prep link
 </button>
 <a
 href={interviewPrepUrl(session.access_token)}
 target="_blank"
 rel="noopener noreferrer"
 className="btn-primary ggj-text-meta w-full justify-center inline-flex items-center gap-1"
 >
 <MonitorPlay className="w-3.5 h-3.5" aria-hidden />
 Open prep
 </a>
 </div>
 </div>
 </CandidateCollapsiblePanel>

 <CandidateCollapsiblePanel
 storageKey={`ai-int-email-${sessionId}-app`}
 title="Application (pipeline)"
 defaultOpen
 >
 {applicationLoading ? (
 <p className="ggj-text-meta vscode-text-tertiary">Loading…</p>
 ) : applicationSnapshot ? (
 <div className="space-y-2 ggj-text-meta">
 <div className="flex justify-between gap-2">
 <span className="vscode-text-tertiary">Stage</span>
 <span className="font-medium vscode-text-primary text-right">
 {PIPELINE_STATUS_LABEL[applicationSnapshot.status]}
 </span>
 </div>
 <div className="flex justify-between gap-2">
 <span className="vscode-text-tertiary">Job</span>
 <span className="vscode-text-primary text-right min-w-0 break-words">{applicationSnapshot.job?.title ?? '—'}</span>
 </div>
 <div className="flex justify-between gap-2">
 <span className="vscode-text-tertiary">Client</span>
 <span className="vscode-text-primary text-right min-w-0 break-words">{applicationSnapshot.client?.name ?? '—'}</span>
 </div>
 <Link
 to={`/applications/${applicationId}`}
 target="_blank"
 rel="noopener noreferrer"
 className="btn-secondary ggj-text-meta w-full justify-center inline-flex items-center gap-1 mt-1 font-semibold"
 >
 Application overview
 <ExternalLink className="w-3 h-3" aria-hidden />
 </Link>
 </div>
 ) : (
 <p className="ggj-text-meta vscode-text-tertiary">Could not load application.</p>
 )}
 </CandidateCollapsiblePanel>

 <CandidateCollapsiblePanel storageKey={`ai-int-email-${sessionId}-ctx`} title="In this session" defaultOpen>
 <p className="ggj-text-meta vscode-text-secondary leading-relaxed mb-2">
 Compose uses the same application id as Pipeline → Emails. After send, the thread list below refreshes; opens and
 replies match the application inbox.
 </p>
 <div className="flex flex-col gap-2">
 <button type="button" className="btn-primary ggj-text-meta w-full justify-center font-semibold" onClick={() => setEmailComposeOpen(true)}>
 Tracked compose
 </button>
 <button type="button" className="btn-secondary ggj-text-meta w-full justify-center" onClick={() => setTab('candidate')}>
 Profile tab
 </button>
 </div>
 </CandidateCollapsiblePanel>
 </aside>
 </div>
 </>
 )}
 </div>
 )}

 {activeTab === 'calendar' && (
 <div className="min-w-0">
 {!applicationId ? (
 <div className="ggj-param-group overflow-hidden p-4 space-y-2">
 <h2 className="ggj-text-body font-semibold uppercase tracking-wide vscode-text-tertiary flex items-center gap-2">
 <Calendar className="w-4 h-4 shrink-0" aria-hidden />
 Schedule calendar
 </h2>
 <p className="ggj-text-body text-amber-700 dark:text-amber-200/90">
 Link an application to this AI interview session to load pipeline interview rounds and the schedule calendar.
 </p>
 </div>
 ) : (
 <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_minmax(280px,360px)] items-start">
 <div className="min-w-0 space-y-4">
 <SessionEmailAttrGroup title="Schedule Calendar">
 <p className="ggj-text-meta vscode-text-tertiary mb-3">
 Click any day to open the same <strong className="text-[var(--vscode-foreground)]">Schedule Interview</strong>{' '}
 wizard as <strong className="text-[var(--vscode-foreground)]">Data → AI interviews → Interview</strong> (fixed slot
 pre-filled to 9:00 local that day when you choose a fixed slot). Dots show rounds with a scheduled time. You stay
 on this page.
 </p>
 {eventsLoading ? (
 <div className="flex justify-center py-12">
 <Loader2 className="w-8 h-8 animate-spin vscode-text-tertiary" aria-hidden />
 </div>
 ) : (
 <InterviewMonthCalendar
 monthAnchor={calendarMonthAnchor}
 onMonthAnchorChange={setCalendarMonthAnchor}
 events={monthCalendarEvents}
 selectedId={selectedCalendarEventId}
 onSelectEvent={(id) => setSelectedCalendarEventId(id)}
 onDayClick={(day) => openAiScheduleWizardForDay(day)}
 allowPastDayClick
 />
 )}
 </SessionEmailAttrGroup>

 {selectedCalEvent ? (
 <SessionEmailAttrGroup title={`Round ${selectedCalEvent.round} — Details`}>
 <dl className="ggj-attr-grid ggj-text-body">
 <SessionEmailAttrRow label="Status">
 <CalendarRoundStatusBadge status={selectedCalEvent.status} />
 </SessionEmailAttrRow>
 <SessionEmailAttrRow label="Scheduled at">
 {selectedCalEvent.scheduled_at ? formatDateTimeGmt(selectedCalEvent.scheduled_at) : '—'}
 </SessionEmailAttrRow>
 <SessionEmailAttrRow label="Duration">
 {selectedCalEvent.duration_minutes != null ? `${selectedCalEvent.duration_minutes} min` : '—'}
 </SessionEmailAttrRow>
 <SessionEmailAttrRow label="Location / link">{selectedCalEvent.location ?? '—'}</SessionEmailAttrRow>
 <SessionEmailAttrRow label="Lead interviewer">{selectedCalEvent.interviewer_name ?? '—'}</SessionEmailAttrRow>
 <SessionEmailAttrRow label="Lead email">{selectedCalEvent.interviewer_email ?? '—'}</SessionEmailAttrRow>
 <SessionEmailAttrRow label="Invite sent">
 {selectedCalEvent.invite_sent_at ? formatDateTimeGmt(selectedCalEvent.invite_sent_at) : '—'}
 </SessionEmailAttrRow>
 <SessionEmailAttrRow label="Scheduling tool">
 {selectedCalEvent.scheduling_provider
 ? selectedCalEvent.scheduling_provider.charAt(0).toUpperCase() + selectedCalEvent.scheduling_provider.slice(1)
 : '—'}
 </SessionEmailAttrRow>
 <SessionEmailAttrRow label="Booking URL">
 {selectedCalEvent.scheduling_booking_url ? (
 <a
 href={selectedCalEvent.scheduling_booking_url}
 target="_blank"
 rel="noreferrer"
 className="text-brand-400 hover:underline inline-flex items-center gap-1 break-all"
 >
 <Link2 className="w-3 h-3 shrink-0" aria-hidden />
 Open link
 </a>
 ) : (
 '—'
 )}
 </SessionEmailAttrRow>
 <SessionEmailAttrRow label="Notes">{selectedCalEvent.notes ?? '—'}</SessionEmailAttrRow>
 {session.pipeline_interview_event_id === selectedCalEvent.id ? (
 <SessionEmailAttrRow label="AI session">
 <span className="text-[10px] font-semibold uppercase text-[var(--vscode-text-link)]">Linked to this AI interview</span>
 </SessionEmailAttrRow>
 ) : null}
 </dl>
 </SessionEmailAttrGroup>
 ) : null}
 </div>

 <aside className="ggj-rail-aside space-y-4 lg:sticky lg:top-4 lg:self-start">
 <CandidateCollapsiblePanel storageKey={`ai-int-cal-${sessionId}-ai`} title="This AI interview" defaultOpen>
 <AiInterviewCalendarRailPanel
 session={session}
 candidate={candidate}
 candidateName={candidateName}
 applicationSnapshot={applicationSnapshot}
 orgId={orgId}
 onCopyPrep={() => void copyPrepLink()}
 onEmailTab={() => setTab('email')}
 onParametersTab={() => setTab('parameters')}
 />
 </CandidateCollapsiblePanel>

 <CandidateCollapsiblePanel storageKey={`ai-int-cal-${sessionId}-links`} title="Quick Links" defaultOpen>
 <div className="space-y-0.5 px-1">
 {calendarQuickLinks.map((l) =>
 l.external ? (
 <a
 key={l.key}
 href={l.to}
 target="_blank"
 rel="noopener noreferrer"
 className="flex items-center justify-between gap-2 py-1.5 ggj-text-body text-brand-400 hover:text-brand-300"
 >
 <span className="truncate">{l.label}</span>
 <ChevronRight className="w-3.5 h-3.5 shrink-0" aria-hidden />
 </a>
 ) : (
 <Link
 key={l.key}
 to={l.to}
 className="flex items-center justify-between gap-2 py-1.5 ggj-text-body text-brand-400 hover:text-brand-300"
 >
 <span className="truncate">{l.label}</span>
 <ChevronRight className="w-3.5 h-3.5 shrink-0" aria-hidden />
 </Link>
 )
 )}
 </div>
 </CandidateCollapsiblePanel>
 </aside>
 </div>
 )}
 </div>
 )}

 {activeTab === 'ai-interviews' && (
 <div className="space-y-3 min-w-0">
 <div className="ggj-param-group overflow-hidden">
 <div className="ggj-profile-toolbar flex-wrap justify-between gap-y-2">
 <span className="ggj-text-meta font-semibold uppercase tracking-wider vscode-text-tertiary inline-flex items-center gap-2">
 <Sparkles className="w-4 h-4 text-[var(--vscode-text-link)] shrink-0" aria-hidden />
 AI interviews
 </span>
 <Link
 to="/ai-interviews"
 className="ggj-text-meta text-[var(--vscode-text-link)] hover:underline font-medium shrink-0"
 >
 Org hub (list) →
 </Link>
 </div>
 <p className="ggj-text-meta vscode-text-secondary leading-relaxed px-1 pb-2">
 Transcripts and recordings (below) include every attempt across this candidate’s AI sessions. This page’s session and any
 others are summarized in the <strong className="text-[var(--vscode-foreground)]">right column</strong>; the main column
 lists compact links to other workspaces when applicable.
 </p>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_min(280px,32%)] gap-3 md:gap-5 items-start">
 <div className="ggj-param-group overflow-hidden p-3 min-w-0">
 <CandidateAiInterviewsPanel
 candidateId={candidate.id}
 candidateName={candidate.full_name ?? candidateName}
 candidateEmail={candidate.email}
 applications={candidateApplications}
 onOpenApplicationsTab={() => setTab('candidate')}
 highlightSessionId={sessionId}
 embedInSessionDetailPage
 />
 </div>
 <aside className="ggj-rail-aside space-y-2 md:sticky md:top-4 self-start min-w-0 max-w-full">
 <CandidateCollapsiblePanel storageKey={`ai-int-session-${sessionId}-ai-tab-rail`} title="This session" defaultOpen>
 <div className="space-y-2 ggj-text-meta">
 <AiInterviewSessionRailRow label="Session">
 <span>
 {ROUND_LABEL[session.session_type] ?? session.session_type} · {aiInterviewSessionStatusLabel(session.status)}
 </span>
 </AiInterviewSessionRailRow>
 <AiInterviewSessionRailRow label="Interviewer">
 <span className="inline-flex items-center gap-1 justify-end">
 <Sparkles className="w-3 h-3 text-violet-400 shrink-0" aria-hidden />
 AI interviewer (browser)
 </span>
 </AiInterviewSessionRailRow>
 <AiInterviewSessionRailRow label="Scheduled by">
 {session.initiator ? (
 <Link
 to={`/team/member/${session.initiator.id}`}
 className="text-[var(--vscode-text-link)] hover:underline font-medium"
 >
 {session.initiator.display_name?.trim() || session.initiator.email || 'Team member'}
 </Link>
 ) : (
 <span className="vscode-text-tertiary italic font-normal">Not recorded</span>
 )}
 </AiInterviewSessionRailRow>
 <AiInterviewSessionRailRow label="Language">{session.language}</AiInterviewSessionRailRow>
 <AiInterviewSessionRailRow label="Application">
 {aiInterviewsTabContextApp ? (
 <>
 <Link
 to={`/applications/${aiInterviewsTabContextApp.id}`}
 className="text-[var(--vscode-text-link)] hover:underline font-medium"
 >
 {aiInterviewsTabContextApp.job?.title ?? 'Application'}
 </Link>{' '}
 <span className="vscode-text-tertiary font-normal">
 ({PIPELINE_STATUS_LABEL[aiInterviewsTabContextApp.status] ?? aiInterviewsTabContextApp.status})
 </span>
 </>
 ) : session.application_id ? (
 <span className="font-mono">{session.application_id.slice(0, 8)}…</span>
 ) : (
 <span className="vscode-text-tertiary italic font-normal">Not linked (legacy)</span>
 )}
 </AiInterviewSessionRailRow>
 <AiInterviewSessionRailRow label="Created">
 <span className="tabular-nums font-normal vscode-text-secondary">{formatDateTimeGmt(session.created_at)}</span>
 </AiInterviewSessionRailRow>
 <AiInterviewSessionRailRow label="Completed at">
 <span className="tabular-nums font-normal vscode-text-secondary">
 {session.completed_at ? formatDateTimeGmt(session.completed_at) : '—'}
 </span>
 </AiInterviewSessionRailRow>
 </div>
 <Link
 to={`/candidates/${candidate.id}?tab=ai-interviews`}
 className="ggj-text-meta text-[var(--vscode-text-link)] hover:underline font-medium inline-block mt-3"
 >
 Candidate record → AI interviews
 </Link>
 </CandidateCollapsiblePanel>

 {otherAiSessionsForRail.length > 0 ? (
 <CandidateCollapsiblePanel
 storageKey={`ai-int-session-${sessionId}-ai-tab-other-sessions`}
 title="Other sessions"
 defaultOpen
 >
 <ul className="space-y-3 list-none m-0 p-0">
 {otherAiSessionsForRail.map((os) => {
 const appRow =
 os.application_id != null
 ? candidateApplications.find((a) => a.id === os.application_id) ?? null
 : null;
 return (
 <li
 key={os.id}
 className="rounded border border-[var(--vscode-border)]/80 bg-[var(--vscode-bg-secondary)]/30 p-2.5 space-y-2 ggj-text-meta"
 >
 <Link
 to={`/ai-interviews/${os.id}?tab=ai-interviews`}
 className="text-[var(--vscode-text-link)] hover:underline font-medium block leading-snug"
 >
 {ROUND_LABEL[os.session_type] ?? os.session_type} · {aiInterviewSessionStatusLabel(os.status)}
 </Link>
 <AiInterviewSessionRailRow label="Interviewer">
 <span className="inline-flex items-center gap-1 justify-end">
 <Sparkles className="w-3 h-3 text-violet-400 shrink-0" aria-hidden />
 AI interviewer (browser)
 </span>
 </AiInterviewSessionRailRow>
 <AiInterviewSessionRailRow label="Scheduled by">
 {os.initiator ? (
 <Link
 to={`/team/member/${os.initiator.id}`}
 className="text-[var(--vscode-text-link)] hover:underline font-medium"
 >
 {os.initiator.display_name?.trim() || os.initiator.email || 'Team member'}
 </Link>
 ) : (
 <span className="vscode-text-tertiary italic font-normal">Not recorded</span>
 )}
 </AiInterviewSessionRailRow>
 <AiInterviewSessionRailRow label="Language">{os.language}</AiInterviewSessionRailRow>
 <AiInterviewSessionRailRow label="Application">
 {appRow ? (
 <>
 <Link
 to={`/applications/${appRow.id}`}
 className="text-[var(--vscode-text-link)] hover:underline font-medium"
 >
 {appRow.job?.title ?? 'Application'}
 </Link>{' '}
 <span className="vscode-text-tertiary font-normal">
 ({PIPELINE_STATUS_LABEL[appRow.status] ?? appRow.status})
 </span>
 </>
 ) : os.application_id ? (
 <span className="font-mono">{os.application_id.slice(0, 8)}…</span>
 ) : (
 <span className="vscode-text-tertiary italic font-normal">Not linked (legacy)</span>
 )}
 </AiInterviewSessionRailRow>
 <AiInterviewSessionRailRow label="Created">
 <span className="tabular-nums font-normal vscode-text-secondary">
 {formatDateTimeGmt(os.created_at)}
 </span>
 </AiInterviewSessionRailRow>
 <AiInterviewSessionRailRow label="Completed at">
 <span className="tabular-nums font-normal vscode-text-secondary">
 {os.completed_at ? formatDateTimeGmt(os.completed_at) : '—'}
 </span>
 </AiInterviewSessionRailRow>
 </li>
 );
 })}
 </ul>
 </CandidateCollapsiblePanel>
 ) : null}
 </aside>
 </div>
 </div>
 )}

 {activeTab === 'parameters' && (
 <div className="space-y-4 min-w-0">
 {/* Object — name, object type, description (matches interview workspace Parameters) */}
 <div className="ggj-param-group overflow-hidden">
 <div className="ggj-param-section-block border-b border-[color-mix(in_srgb,var(--vscode-border-primary)_55%,transparent)]">
 <div className="ggj-profile-section-h">Object</div>
 <div className="ggj-profile-section-body">
 <dl className="ggj-attr-grid ggj-text-body">
 <dt className={PARAM_DT}>Name</dt>
 <dd className={PARAM_DD}>
 <span className="font-medium">
 {[candidateName, applicationSnapshot?.job?.title].filter(Boolean).join(' — ') || '—'}
 </span>
 </dd>
 <dt className={PARAM_DT}>Object type</dt>
 <dd className={`${PARAM_DD} vscode-text-secondary`}>AI interview session (read-only)</dd>
 <dt className={PARAM_DT}>Description</dt>
 <dd className={`${PARAM_DD} min-w-0 lg:col-span-3`}>
 <textarea
 readOnly
 tabIndex={-1}
 rows={5}
 value={
 candidate.notes?.trim()
 ? candidate.notes
 : [
 `Browser AI interview (${ROUND_LABEL[session.session_type] ?? session.session_type}).`,
 session.application_id ? 'Linked to a pipeline application when present.' : 'No application linked on this session row.',
 ].join(' ')
 }
 placeholder="—"
 aria-label="Description"
 className="ggj-param-readonly-textarea"
 />
 </dd>
 </dl>
 </div>
 </div>
 </div>

 <div className="ggj-param-group">
 <div className="ggj-profile-section-h">AI interview session</div>
 <div className="ggj-profile-section-body">
 <dl className="ggj-attr-grid ggj-text-body">
 <dt className={PARAM_DT}>Session ID</dt>
 <dd className={PARAM_DD}>
 <code className="ggj-text-meta break-all">{session.id}</code>
 </dd>
 <dt className={PARAM_DT}>Access token</dt>
 <dd className={PARAM_DD}>
 <code className="ggj-text-meta break-all">{`${session.access_token.slice(0, 8)}…`}</code>
 </dd>
 <dt className={PARAM_DT}>Round</dt>
 <dd className={PARAM_DD}>{ROUND_LABEL[session.session_type] ?? session.session_type}</dd>
 <dt className={PARAM_DT}>Language</dt>
 <dd className={PARAM_DD}>{session.language}</dd>
 <dt className={PARAM_DT}>Status</dt>
 <dd className={PARAM_DD}><span className="capitalize">{session.status.replace(/_/g, ' ')}</span></dd>
 <dt className={PARAM_DT}>Expires</dt>
 <dd className={PARAM_DD}>{session.expires_at ? fmtDateLong(session.expires_at) : '—'}</dd>
 <dt className={PARAM_DT}>Completed</dt>
 <dd className={PARAM_DD}>{session.completed_at ? fmtDateLong(session.completed_at) : '—'}</dd>
 </dl>
 </div>
 </div>

 <div className="ggj-param-group">
 <div className="ggj-profile-section-h">Candidate</div>
 <div className="ggj-profile-section-body">
 <dl className="ggj-attr-grid ggj-text-body">
 <dt className={PARAM_DT}>Candidate ID</dt>
 <dd className={PARAM_DD}>
 <Link to={`/candidates/${session.candidate_id}`} className="text-brand-400 hover:underline">
 <code className="ggj-text-meta break-all">{session.candidate_id}</code>
 </Link>
 </dd>
 <dt className={PARAM_DT}>Full name</dt>
 <dd className={PARAM_DD}>{candidate.full_name ?? session.candidate?.full_name ?? '—'}</dd>
 <dt className={PARAM_DT}>Email</dt>
 <dd className={PARAM_DD}>
 {candidate.email || session.candidate?.email ? (
 <a
 href={`mailto:${(candidate.email || session.candidate?.email)!}`}
 className="text-brand-400 hover:underline"
 >
 {candidate.email || session.candidate?.email}
 </a>
 ) : (
 '—'
 )}
 </dd>
 </dl>
 </div>
 </div>

 {applicationSnapshot ? (
 <div className="ggj-param-group">
 <div className="ggj-profile-section-h">
 Application parameters — {applicationSnapshot.job?.title ?? 'Application'}
 </div>
 <div className="ggj-profile-section-body">
 <dl className="ggj-attr-grid ggj-text-body">
 <dt className={PARAM_DT}>Application ID</dt>
 <dd className={PARAM_DD}>
 <Link to={`/applications/${applicationSnapshot.id}`} className="text-brand-400 hover:underline">
 <code className="ggj-text-meta break-all">{applicationSnapshot.id}</code>
 </Link>
 </dd>
 <dt className={PARAM_DT}>Reference</dt>
 <dd className={PARAM_DD}>
 {buildApplicationRefDisplay(applicationSnapshot.id, applicationSnapshot.client_id)}
 </dd>
 <dt className={PARAM_DT}>Pipeline stage</dt>
 <dd className={PARAM_DD}>{PIPELINE_STATUS_LABEL[applicationSnapshot.status]}</dd>
 <dt className={PARAM_DT}>Source</dt>
 <dd className={PARAM_DD}>{applicationSnapshot.source ?? '—'}</dd>
 <dt className={PARAM_DT}>Fitment score</dt>
 <dd className={PARAM_DD}>{applicationSnapshot.fitment_score ?? '—'}</dd>
 <dt className={PARAM_DT}>Assigned to</dt>
 <dd className={PARAM_DD}>
 {applicationSnapshot.assigned_user?.display_name ??
 applicationSnapshot.assigned_user?.email ??
 '—'}
 </dd>
 <dt className={PARAM_DT}>Submitted</dt>
 <dd className={PARAM_DD}>{fmtDateLong(applicationSnapshot.submitted_at)}</dd>
 <dt className={PARAM_DT}>Last updated</dt>
 <dd className={PARAM_DD}>{fmtDateLong(applicationSnapshot.updated_at)}</dd>
 </dl>
 </div>
 </div>
 ) : applicationId ? (
 <div className="ggj-param-group">
 <div className="ggj-profile-section-h">Application</div>
 <div className="ggj-profile-section-body">
 <p className="ggj-text-body vscode-text-secondary">
 {applicationLoading ? 'Loading application…' : 'Application details could not be loaded.'}
 </p>
 <dl className="ggj-attr-grid ggj-text-body mt-2">
 <dt className={PARAM_DT}>Application ID</dt>
 <dd className={PARAM_DD}>
 <code className="ggj-text-meta break-all">{applicationId}</code>
 </dd>
 </dl>
 </div>
 </div>
 ) : null}

 <div className="ggj-param-group">
 <div className="ggj-profile-section-h">Pipeline linking</div>
 <div className="ggj-profile-section-body">
 <dl className="ggj-attr-grid ggj-text-body">
 <dt className={PARAM_DT}>Pipeline interview event ID</dt>
 <dd className={PARAM_DD}>
 {session.pipeline_interview_event_id ? (
 applicationId ? (
 <Link
 to={`/applications/${applicationId}?tab=interviews`}
 className="text-brand-400 hover:underline"
 >
 <code className="ggj-text-meta break-all">{session.pipeline_interview_event_id}</code>
 </Link>
 ) : (
 <code className="ggj-text-meta break-all">{session.pipeline_interview_event_id}</code>
 )
 ) : (
 '—'
 )}
 </dd>
 <dt className={PARAM_DT}>Linked round status</dt>
 <dd className={PARAM_DD}>
 {session.pipeline_interview_event?.status ? (
 <span className="capitalize">{session.pipeline_interview_event.status.replace(/_/g, ' ')}</span>
 ) : (
 '—'
 )}
 </dd>
 <dt className={PARAM_DT}>Linked slot (scheduled)</dt>
 <dd className={PARAM_DD}>
 {session.pipeline_interview_event?.scheduled_at
 ? fmtDateLong(session.pipeline_interview_event.scheduled_at)
 : '—'}
 </dd>
 </dl>
 </div>
 </div>

 {applicationSnapshot?.job ? (
 <div className="ggj-param-group">
 <div className="ggj-profile-section-h">Job parameters</div>
 <div className="ggj-profile-section-body">
 <dl className="ggj-attr-grid ggj-text-body">
 <dt className={PARAM_DT}>Job ID</dt>
 <dd className={PARAM_DD}>
 <code className="ggj-text-meta break-all">{applicationSnapshot.job.id}</code>
 </dd>
 <dt className={PARAM_DT}>Title</dt>
 <dd className={PARAM_DD}>
 <Link to={`/jobs/${applicationSnapshot.job.id}`} className="text-brand-400 hover:underline">
 {applicationSnapshot.job.title}
 </Link>
 </dd>
 <dt className={PARAM_DT}>Status</dt>
 <dd className={PARAM_DD}>
 <span className="capitalize">{applicationSnapshot.job.status ?? '—'}</span>
 </dd>
 <dt className={PARAM_DT}>Location</dt>
 <dd className={PARAM_DD}>{applicationSnapshot.job.location ?? '—'}</dd>
 <dt className={PARAM_DT}>Employment type</dt>
 <dd className={PARAM_DD}>{applicationSnapshot.job.employment_type ?? '—'}</dd>
 </dl>
 </div>
 </div>
 ) : null}

 {applicationSnapshot?.client ? (
 <div className="ggj-param-group">
 <div className="ggj-profile-section-h">Client parameters</div>
 <div className="ggj-profile-section-body">
 <dl className="ggj-attr-grid ggj-text-body">
 <dt className={PARAM_DT}>Client ID</dt>
 <dd className={PARAM_DD}>
 <code className="ggj-text-meta break-all">{applicationSnapshot.client.id}</code>
 </dd>
 <dt className={PARAM_DT}>Name</dt>
 <dd className={PARAM_DD}>
 <Link to={`/clients/${applicationSnapshot.client.id}`} className="text-brand-400 hover:underline">
 {applicationSnapshot.client.name}
 </Link>
 </dd>
 <dt className={PARAM_DT}>Industry</dt>
 <dd className={PARAM_DD}>{applicationSnapshot.client.industry ?? '—'}</dd>
 <dt className={PARAM_DT}>Location</dt>
 <dd className={PARAM_DD}>{applicationSnapshot.client.location ?? '—'}</dd>
 </dl>
 </div>
 </div>
 ) : null}

 <div className="ggj-param-group">
 <div className="ggj-profile-section-h">System / Audit</div>
 <div className="ggj-profile-section-body">
 <dl className="ggj-attr-grid ggj-text-body">
 <dt className={PARAM_DT}>Session ID</dt>
 <dd className={PARAM_DD}>
 <code className="ggj-text-meta break-all">{session.id}</code>
 </dd>
 <dt className={PARAM_DT}>Application ID</dt>
 <dd className={PARAM_DD}>
 {session.application_id ? (
 <code className="ggj-text-meta break-all">{session.application_id}</code>
 ) : (
 '—'
 )}
 </dd>
 <dt className={PARAM_DT}>Candidate ID</dt>
 <dd className={PARAM_DD}>
 <code className="ggj-text-meta break-all">{session.candidate_id}</code>
 </dd>
 <dt className={PARAM_DT}>Job ID</dt>
 <dd className={PARAM_DD}>
 {session.job_id ? <code className="ggj-text-meta break-all">{session.job_id}</code> : '—'}
 </dd>
 <dt className={PARAM_DT}>Client ID</dt>
 <dd className={PARAM_DD}>
 {applicationSnapshot?.client_id ? (
 <code className="ggj-text-meta break-all">{applicationSnapshot.client_id}</code>
 ) : (
 '—'
 )}
 </dd>
 <dt className={PARAM_DT}>Org ID</dt>
 <dd className={PARAM_DD}>
 <code className="ggj-text-meta break-all">{orgId ?? '—'}</code>
 </dd>
 <dt className={PARAM_DT}>Created by</dt>
 <dd className={PARAM_DD}>
 {session.initiator?.display_name?.trim() ||
 session.initiator?.email ||
 (session.created_by_org_user_id ? (
 <code className="ggj-text-meta break-all">{session.created_by_org_user_id}</code>
 ) : (
 '—'
 ))}
 </dd>
 <dt className={PARAM_DT}>Created</dt>
 <dd className={PARAM_DD}>{fmtDateLong(session.created_at)}</dd>
 <dt className={PARAM_DT}>Last modified</dt>
 <dd className={PARAM_DD}>{fmtDateLong(session.updated_at)}</dd>
 <dt className={PARAM_DT}>Modified by</dt>
 <dd className={`${PARAM_DD} vscode-text-secondary`}>Not stored</dd>
 </dl>
 </div>
 </div>
 </div>
 )}

 {applicationSnapshot && applicationId && user?.orgId ? (
 <AiInterviewScheduleWizardModal
 open={aiScheduleWizardOpen}
 onClose={() => {
 setAiScheduleWizardOpen(false);
 setAiScheduleWizardDayKey(null);
 }}
 orgId={user.orgId}
 orgName={user.orgName}
 recruiterName={user.name}
 orgUserId={user.orgUserId}
 applications={[applicationSnapshot]}
 scheduleDayKey={aiScheduleWizardDayKey}
 initialApplicationId={applicationId}
 onScheduled={() => {
 void refreshThreadsAndEvents();
 void reloadSession();
 setAiScheduleWizardOpen(false);
 setAiScheduleWizardDayKey(null);
 }}
 />
 ) : null}
 </div>
 );
}

/* ------------------------------------------------------------------ */
/* Phase 8 T-12 §12 E–G — Post-interview summary panel (re-skin). */
/* ------------------------------------------------------------------ */

interface InterviewReviewPanelProps {
 session: AiInterviewSessionHubDetailRow;
 candidate: Candidate;
 candidateName: string;
 applicationId: string | null;
 applicationSnapshot: Application | null;
 reviewDecisionsEnabled: boolean;
 reviewBusy: boolean;
 onReviewApprove: () => Promise<void> | void;
 onReviewReject: () => Promise<void> | void;
 onReviewEscalate: () => Promise<void> | void;
 summaryTab: InterviewSummaryTabId;
 onSummaryTabChange: (t: InterviewSummaryTabId) => void;
 onTemplateChange: (templateLabel: string) => Promise<void>;
 companyName: string;
 prepUrl: string;
}

const TEMPLATE_OPTIONS = ['Screening call', 'Technical call', 'Behavioral call', 'General'];

function InterviewReviewPanel({
 session,
 candidate,
 candidateName,
 applicationId,
 applicationSnapshot,
 reviewDecisionsEnabled,
 reviewBusy,
 onReviewApprove,
 onReviewReject,
 onReviewEscalate,
 summaryTab,
 onSummaryTabChange,
 onTemplateChange,
 companyName,
 prepUrl,
}: InterviewReviewPanelProps) {
 const attempts = useMemo(() => listInterviewAttempts(session), [session]);
 const latestRecordingPath = useMemo(() => {
 for (let i = attempts.length - 1; i >= 0; i -= 1) {
 const a = attempts[i];
 if (a?.recordingPath) return a.recordingPath;
 }
 return null;
 }, [attempts]);

 const [downloading, setDownloading] = useState(false);
 const [downloadError, setDownloadError] = useState<string | null>(null);

 const handleDownloadRecording = useCallback(async () => {
 if (!latestRecordingPath) return;
 setDownloading(true);
 setDownloadError(null);
 try {
 const url = await aiInterviewSessionService.getRecordingSignedUrl(latestRecordingPath);
 window.open(url, '_blank', 'noopener,noreferrer');
 } catch (e) {
 setDownloadError(e instanceof Error ? e.message : 'Could not load recording.');
 } finally {
 setDownloading(false);
 }
 }, [latestRecordingPath]);

 const pipelineFooter = (
 <div
 className="sticky bottom-0 z-10 flex flex-wrap items-center justify-end gap-2 rounded-[12px] border px-3 py-2.5 backdrop-blur-sm"
 style={{
 borderColor: 'var(--vscode-border-primary)',
 background: 'color-mix(in srgb, var(--vscode-bg-primary) 92%, transparent)',
 }}
 >
 <span className="mr-auto min-w-0 max-w-[22rem] truncate text-[12px] vscode-text-tertiary">
 {!applicationId
 ? 'Link an application to enable Shortlist, Reject, and On-hold.'
 : !reviewDecisionsEnabled
 ? 'Pipeline actions unlock when the interview is complete and not in a terminal stage.'
 : applicationSnapshot
 ? `Linked application · ${PIPELINE_STATUS_LABEL[applicationSnapshot.status] ?? applicationSnapshot.status}`
 : 'Loading application…'}
 </span>
 <button
 type="button"
 className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border bg-transparent px-3 text-[13px] font-medium vscode-text-primary transition-colors hover:bg-[var(--vscode-bg-hover)] disabled:cursor-not-allowed disabled:opacity-50"
 style={{ borderColor: 'var(--vscode-border-primary)' }}
 disabled={!reviewDecisionsEnabled || reviewBusy}
 onClick={() => void onReviewEscalate()}
 >
 {reviewBusy ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden /> : <Flag className="h-3.5 w-3.5 shrink-0" aria-hidden />}
 On-hold
 </button>
 <button
 type="button"
 className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border bg-transparent px-3 text-[13px] font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
 style={{ borderColor: 'color-mix(in srgb, #EF4444 35%, transparent)' }}
 disabled={!reviewDecisionsEnabled || reviewBusy}
 onClick={() => void onReviewReject()}
 >
 {reviewBusy ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden /> : <XCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />}
 Reject
 </button>
 <button
 type="button"
 className="inline-flex h-9 items-center gap-1.5 rounded-[10px] px-3.5 text-[13px] font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
 style={{ background: 'var(--ggj-accent,#10B981)' }}
 disabled={!reviewDecisionsEnabled || reviewBusy}
 onClick={() => void onReviewApprove()}
 >
 {reviewBusy ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden /> : <CheckCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />}
 Shortlist
 </button>
 </div>
 );

 return (
 <div className="relative min-w-0">
 {/* Page header row: breadcrumb-ish title + candidate chip + Download recording */}
 <div className="mb-4 flex flex-wrap items-center gap-3">
 <nav
 className="flex min-w-0 flex-1 items-center gap-1.5 text-[12px] vscode-text-secondary"
 aria-label="Interview breadcrumb"
 >
 <Link to="/ai-interviews" className="text-[color:var(--vscode-text-link)] hover:underline">
 Interviews
 </Link>
 <ChevronRight className="h-3 w-3 vscode-text-tertiary" aria-hidden />
 <span className="truncate vscode-text-primary">
 {companyName ? `${companyName} ↔ ${candidateName}` : candidateName}
 </span>
 </nav>
 <span
 className="inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[12px] font-medium vscode-text-primary"
 style={{ borderColor: 'var(--vscode-border-primary)' }}
 >
 <span
 className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold uppercase"
 style={{ background: 'var(--ggj-accent-soft,#D1FAE5)', color: '#065F46' }}
 aria-hidden
 >
 {recordInitials(candidateName)}
 </span>
 {candidateName}
 </span>
 <button
 type="button"
 onClick={() => void handleDownloadRecording()}
 disabled={!latestRecordingPath || downloading}
 title={latestRecordingPath ? 'Open the latest recording in a new tab' : 'No recording available'}
 className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border bg-transparent px-3 text-[13px] font-medium vscode-text-primary transition-colors hover:bg-[var(--vscode-bg-hover)] disabled:cursor-not-allowed disabled:opacity-50"
 style={{ borderColor: 'var(--vscode-border-primary)' }}
 >
 {downloading ? (
 <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
 ) : (
 <Download className="h-3.5 w-3.5 shrink-0" aria-hidden />
 )}
 Download recording
 </button>
 </div>

 {downloadError ? (
 <p className="mb-3 rounded-[10px] border border-red-500/30 bg-red-50 px-3 py-2 text-[12px] text-red-700 dark:bg-red-950/25 dark:text-red-300">
 {downloadError}
 </p>
 ) : null}

 <div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
 <div className="min-w-0">
          <InterviewSummaryTabs
            session={session}
            activeTab={summaryTab}
            onTabChange={onSummaryTabChange}
            pipelineFooter={pipelineFooter}
            templateLabel={ROUND_LABEL[session.session_type] ? `${ROUND_LABEL[session.session_type]} call` : 'Screening call'}
            templateOptions={TEMPLATE_OPTIONS}
            onTemplateChange={(label) => void onTemplateChange(label)}
          />
 </div>
 <div className="lg:sticky lg:top-4 self-start h-[calc(100dvh-200px)] min-h-[520px]">
 <InterviewTemplateRail
 sessionId={session.id}
 candidateId={candidate.id}
 candidateName={candidateName}
 details={{
 meetingTitle: `${ROUND_LABEL[session.session_type] ?? session.session_type} — ${candidateName}`,
 meetingUrl: prepUrl,
 sessionType: ROUND_LABEL[session.session_type] ?? session.session_type,
 language: session.language,
 companyName: companyName || '—',
 candidateName,
 candidateEmail: candidate.email,
 jobTitle: applicationSnapshot?.job?.title ?? null,
 scheduledAt: session.pipeline_interview_event?.scheduled_at
 ? formatDateTimeGmt(session.pipeline_interview_event.scheduled_at)
 : null,
 completedAt: session.completed_at ? formatDateTimeGmt(session.completed_at) : null,
 }}
 />
 </div>
 </div>
 </div>
 );
}
